// src/whatsappChat/components/ConversationList.jsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faClock, faPlus } from '@fortawesome/free-solid-svg-icons';
import { normalizeError } from '../api/client';
import { formatListTime, formatPhone, initials, numberLabel, readableText } from '../api/content';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'unknown', label: 'Unknown' },
];

function ConversationRow({ row, active, showNumber, onSelect }) {
  const title = row.contact_name || formatPhone(row.phone);
  const unread = row.unread_count > 0;

  return (
    <button
      type="button"
      className={`wa-row${active ? ' is-active' : ''}${unread ? ' is-unread' : ''}`}
      onClick={() => onSelect(row.id)}
    >
      <span className={`wa-avatar${row.subscriber_id ? '' : ' is-unknown'}`}>
        {initials(row.contact_name, row.phone)}
      </span>
      <span className="wa-row-main">
        <span className="wa-row-top">
          <span className="wa-row-name">{title}</span>
          <span className="wa-row-time">{formatListTime(row.last_message_at)}</span>
        </span>
        <span className="wa-row-bottom">
          <span className="wa-row-preview">
            {row.last_direction === 'outbound' && <span className="wa-row-you">You: </span>}
            {readableText(row.last_message_preview) ||' '}
          </span>
          {unread && (
            <span className="wa-unread">{row.unread_count > 99 ? '99+' : row.unread_count}</span>
          )}
        </span>
        <span className="wa-row-tags">
          {showNumber && row.number_name && <span className="wa-tag wa-tag-number">{row.number_name}</span>}
          {!row.number_name && <span className="wa-tag wa-tag-danger">Number removed</span>}
          {!row.subscriber_id && <span className="wa-tag">Unknown</span>}
          {!row.window_open && (
            <span className="wa-tag wa-tag-warning" title="24-hour window closed">
              <FontAwesomeIcon icon={faClock} /> Closed
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

export function ConversationList({
  query,
  activeId,
  onSelect,
  search,
  onSearch,
  filter,
  onFilter,
  numbers,
  numberId,
  onNumber,
  showUnknown,
  onNewChat,
}) {
  const { conversations, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = query;
  const filters = FILTERS.filter((f) => f.value !== 'unknown' || showUnknown);
  const showNumber = numbers.length > 1 && !numberId;

  return (
    <aside className="wa-list">
      <div className="wa-list-head">
        <div className="wa-list-top">
          <label className="wa-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="search"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search name or phone"
              aria-label="Search chats"
            />
          </label>
          {onNewChat && (
            <button type="button" className="wa-btn wa-btn-primary" onClick={onNewChat}>
              <FontAwesomeIcon icon={faPlus} /> New
            </button>
          )}
        </div>

        <div className="wa-list-filters">
          <div className="wa-tabs" role="tablist">
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={filter === f.value}
                className={`wa-tab${filter === f.value ? ' is-active' : ''}`}
                onClick={() => onFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {numbers.length > 1 && (
            <select
              className="wa-select"
              value={numberId}
              onChange={(e) => onNumber(e.target.value)}
              aria-label="Business number"
            >
              <option value="">All numbers</option>
              {numbers.map((n) => (
                <option key={n.number_id} value={n.number_id}>
                  {numberLabel(n)}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="wa-list-body">
        {isLoading ? (
          <div className="wa-state">Loading chats…</div>
        ) : isError && conversations.length === 0 ? (
          <div className="wa-state">
            <p>{normalizeError(error).message}</p>
            <button type="button" className="wa-btn wa-btn-secondary" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="wa-state">
            {search ? 'No chats match your search.' : filter === 'unread' ? 'No unread chats.' : 'No chats yet.'}
          </div>
        ) : (
          <>
            {conversations.map((row) => (
              <ConversationRow
                key={row.id}
                row={row}
                active={row.id === activeId}
                showNumber={showNumber}
                onSelect={onSelect}
              />
            ))}
            {hasNextPage && (
              <div className="wa-list-more">
                <button
                  type="button"
                  className="wa-btn wa-btn-secondary"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
